import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { EmptyState } from "@freightsimple/ui-core/Display/EmptyState";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/ClockCounterClockwise";
import { type TrackingLine } from "@freightsimple/generated-carriers-openapi-client/models/TrackingLine";

/**
 * The shipment's tracking timeline, including the lines this carrier posted
 * itself when answering an operational issue.
 *
 * Showing their own updates back matters: a carrier who reports a delay through
 * the portal and then can't see it on the shipment has no reason to believe it
 * landed, and will send the email anyway.
 *
 * Newest first — the current state of the freight is what someone opening this
 * tab wants, not where it started.
 */
export function ShipmentTrackingTab({
  lines,
}: {
  lines: TrackingLine[] | undefined;
}) {
  if (lines === undefined) {
    return (
      <Tab id="tracking" title="Tracking">
        <T size="sm" color="secondary">
          Loading…
        </T>
      </Tab>
    );
  }

  const newestFirst = [...lines].reverse();

  return (
    <Tab id="tracking" title="Tracking" count={lines.length}>
      {lines.length === 0 ? (
        <EmptyState
          icon={ClockCounterClockwiseIcon}
          title="No tracking yet"
          description="Updates appear here as they come in, including anything you post against an open issue."
        />
      ) : (
        <Stack gap={4}>
          {newestFirst.map((line) => (
            <Stack gap={1} key={line.trackingLineId}>
              <T size="sm" weight="medium">
                {line.message}
              </T>
              {line.details && (
                <T size="sm" color="secondary">
                  {line.details}
                </T>
              )}
              <Stack gap={1}>
                {line.at && (
                  <T size="sm" color="muted">
                    {line.at}
                  </T>
                )}
                {line.source && <Badge label={line.source} variant="neutral" />}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Tab>
  );
}
